import { ComponentStore } from '@ngrx/component-store';
import { Inject, Injectable, InjectionToken } from '@angular/core';

import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { Observable, EMPTY, from } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { Option } from '@telespot/protocols/data-access';
import {
  CounterMode,
  DrawingMode,
  ETabContext,
  ImageFilter,
  ImageFilterType,
  RulerType,
  ViewerMode,
} from '../../types/viewer.types';
import type { FindingParameters } from '@telespot/domain';
import { Analyst } from '@analysis/domain';
import { GetAssetStatusUseCase } from '@analysis/use-cases';

// FIX: Remove this import when we have a proper stats mode component.
import { StatsMode } from '../../components/stats-settings/stats-settings.component';

export interface BoxDimension {
  defaultWidth: number;
  defaultHeight: number;
}

// Constants for default box dimensions
export const DEFAULT_BOX_WIDTH = 100;
export const DEFAULT_BOX_HEIGHT = 100;

export interface AssetFilters {
  analyzed: boolean;
  favAcquisition: boolean;
  favAnalysis: boolean;
}

export interface ILabel extends Option {
  category: string;
  stepId: string;
  pipelineId: string;
}

export interface MicroLayoutState {
  protocolPanelCollapsed: boolean;
  acquisitionEdition: boolean;
  viewerMode: ViewerMode;
  drawingMode: DrawingMode;
  rulerType: RulerType;
  rulerSize: number | null;
  imageFilters: ImageFilter[];
  selectedPreset: number | null;
  hiddenLabels: Set<string>;
  pinnedLabels: Set<string>;
  selectedLabels: Set<string>;
  showPinnedOnly: boolean;
  collapsedCategories: Set<string>;
  multiSelection: boolean;
  counterMode: CounterMode;
  tabContext: ETabContext | undefined;
  assetFilters: AssetFilters;
  analyzedAssets: Set<string>;
  reviewedAssets: Set<string>;
  viewedAssets: Set<string>;
  bookmarkedAsset: string | null;
  selectedRois: Set<string>;
  editingEnabled: boolean;
  boxDimensions: Map<string, BoxDimension>;
  multiSelectionBoxDimensions: Map<string, BoxDimension>;
  statsMode: StatsMode;
}

const initialState: MicroLayoutState = {
  protocolPanelCollapsed: false,
  acquisitionEdition: false,
  viewerMode: ViewerMode.idle,
  drawingMode: DrawingMode.center,
  rulerType: RulerType.NONE,
  rulerSize: null,
  imageFilters: [
    { filter: ImageFilterType.BRIGHTNESS, value: 0 },
    { filter: ImageFilterType.CONTRAST, value: 0 },
    { filter: ImageFilterType.SATURATION, value: 0 },
    { filter: ImageFilterType.NEGATIVE, value: 0 },
  ],
  selectedPreset: null,
  hiddenLabels: new Set(),
  pinnedLabels: new Set(),
  selectedLabels: new Set(),
  showPinnedOnly: false,
  collapsedCategories: new Set(),
  multiSelection: false,
  counterMode: CounterMode.counter,
  tabContext: undefined,
  assetFilters: {
    analyzed: false,
    favAcquisition: false,
    favAnalysis: false,
  },
  analyzedAssets: new Set(),
  reviewedAssets: new Set(),
  viewedAssets: new Set(),
  bookmarkedAsset: null,
  selectedRois: new Set(),
  editingEnabled: false,
  boxDimensions: new Map(),
  multiSelectionBoxDimensions: new Map(),
  statsMode: StatsMode.COMPLETE,
};

export interface PresetFilters {
  preset: number;
  filters: ImageFilter[];
}

export const GET_ASSET_STATUS_USE_CASE = new InjectionToken<GetAssetStatusUseCase>('GET_ASSET_STATUS_USE_CASE');

@Injectable()
export class MicroLayoutStore extends ComponentStore<MicroLayoutState> {
  constructor(
    @Inject(GET_ASSET_STATUS_USE_CASE) private readonly getAssetStatusUseCase: GetAssetStatusUseCase
  ) {
    super(initialState);
  }

  public readonly acquisitionEdition$ = this.select((state) => state.acquisitionEdition);
  // Panel selectors
  public readonly protocolPanelCollapsed$ = this.select((state) => state.protocolPanelCollapsed);

  // Viewer states
  public readonly viewerMode$ = this.select((state) => state.viewerMode);
  public readonly drawingMode$ = this.select((state) => state.drawingMode);

  // Tool states
  public readonly rulerType$ = this.select((state) => state.rulerType);
  public readonly rulerSize$ = this.select((state) => state.rulerSize);

  // Image filter selectors
  public readonly imageFilters$ = this.select((state) => state.imageFilters);
  public readonly selectedPreset$ = this.select((state) => state.selectedPreset);

  // Label selectors
  public readonly hiddenLabels$ = this.select((state) => state.hiddenLabels);
  public readonly pinnedLabels$ = this.select((state) => state.pinnedLabels);
  public readonly selectedLabels$ = this.select((state) => state.selectedLabels);
  public readonly showPinnedOnly$ = this.select((state) => state.showPinnedOnly);

  // Category selectors
  public readonly collapsedCategories$ = this.select((state) => state.collapsedCategories);

  // Selection selectors
  public readonly multiSelection$ = this.select((state) => state.multiSelection);
  public readonly counterMode$ = this.select((state) => state.counterMode);

  // Tab context selectors
  public readonly tabContext$ = this.select((state) => state.tabContext);

  // UI selectors
  public readonly editingEnabled$ = this.select((state) => state.editingEnabled);

  public readonly assetFilters$ = this.select((state) => state.assetFilters);
  public readonly analyzedAssets$ = this.select((state) => state.analyzedAssets);
  public readonly reviewedAssets$ = this.select((state) => state.reviewedAssets);
  public readonly viewedAssets$ = this.select((state) => state.viewedAssets);
  public readonly bookmarkedAsset$ = this.select((state) => state.bookmarkedAsset);

  public readonly selectedRois$ = this.select((state) => state.selectedRois);

  public readonly boxDimensions$ = this.select((state) => state.boxDimensions);
  public readonly multiSelectionBoxDimensions$ = this.select((state) => state.multiSelectionBoxDimensions);
  public readonly statsMode$ = this.select((state) => state.statsMode);

  // #endregion

  public readonly availableDrawingModes$ = this.viewerMode$.pipe(
    map((mode) => {
      switch (mode) {
        case ViewerMode.drawing:
          return [DrawingMode.center, DrawingMode.boundingBox];
        case ViewerMode.free:
          return [DrawingMode.sketch, DrawingMode.lasso];
        default:
          return [];
      }
    })
  );

  public readonly cursorSelector$ = this.select(
    this.viewerMode$,
    this.drawingMode$,
    (viewerMode, drawingMode): string | undefined => {
      switch (viewerMode) {
        case ViewerMode.idle:
          return 'cursor-move';
        case ViewerMode.selecting:
          return 'cursor-pointer';
        case ViewerMode.drawing:
          switch (drawingMode) {
            case DrawingMode.center:
              return 'cursor-poi';
            case DrawingMode.boundingBox:
              return 'cursor-crosshair';
            default:
              return undefined;
          }
        case ViewerMode.removing:
        case ViewerMode.free:
          return 'cursor-crosshair';
        default:
          return undefined;
      }
    }
  );

  public readonly rulerActive$ = this.rulerType$.pipe(map((rulerType) => rulerType !== RulerType.NONE));

  // #endregion

  // #region Updaters

  public readonly toggleProtocolPanel = this.updater((state) => ({
    ...state,
    protocolPanelCollapsed: !state.protocolPanelCollapsed,
  }));

  public readonly setProtocolPanelCollapsed = this.updater((state, collapsed: boolean) => ({
    ...state,
    protocolPanelCollapsed: collapsed,
  }));

  // Viewer updaters
  public readonly setViewerMode = this.updater((state, mode: ViewerMode) => ({
    ...state,
    viewerMode: mode,
  }));

  public readonly setDrawingMode = this.updater((state, mode: DrawingMode) => ({
    ...state,
    drawingMode: mode,
  }));

  public readonly setRulerType = this.updater((state, type: RulerType) => ({
    ...state,
    rulerType: type,
  }));

  public readonly setRulerSize = this.updater((state, size: number | null) => ({
    ...state,
    rulerSize: size,
  }));

  public readonly resetRuler = this.updater((state) => ({
    ...state,
    rulerType: RulerType.NONE,
    rulerSize: null,
  }));

  // Image filter updaters
  public readonly updateImageFilter = this.updater((state, filter: ImageFilter) => ({
    ...state,
    selectedPreset: null,
    imageFilters: state.imageFilters.map((f) => (f.filter === filter.filter ? { ...f, value: filter.value } : f)),
  }));

  public readonly updateImageFiltersBulk = this.updater((state, filters: ImageFilter[]) => ({
    ...state,
    imageFilters: state.imageFilters.map((f) => {
      const match = filters.find((x) => x.filter === f.filter);
      return match ? { ...f, value: match.value } : f;
    }),
  }));

  public readonly resetImageFilters = this.updater((state) => ({
    ...state,
    selectedPreset: null,
    imageFilters: state.imageFilters.map((f) => ({ ...f, value: 0 })),
  }));

  public readonly setSelectedPreset = this.updater((state, preset: number | null) => ({
    ...state,
    selectedPreset: preset,
  }));

  // Label updaters
  public readonly toggleHiddenLabel = this.updater((state, uuid: string) => {
    const updatedHidden = new Set(state.hiddenLabels);
    updatedHidden.has(uuid) ? updatedHidden.delete(uuid) : updatedHidden.add(uuid);
    return {
      ...state,
      hiddenLabels: updatedHidden,
    };
  });

  public readonly togglePinnedLabel = this.updater((state, uuid: string) => {
    const updatedPinned = new Set(state.pinnedLabels);
    updatedPinned.has(uuid) ? updatedPinned.delete(uuid) : updatedPinned.add(uuid);
    return {
      ...state,
      pinnedLabels: updatedPinned,
    };
  });

  public readonly clearSelectedLabels = this.updater((state) => ({
    ...state,
    selectedLabels: new Set(),
  }));

  public readonly selectLabel = this.updater((state, action: { uuid: string; replace?: boolean }) => {
    const { uuid, replace = false } = action;
    const updatedSelectedLabels = replace ? new Set([uuid]) : new Set(state.selectedLabels);

    if (!replace) {
      updatedSelectedLabels.has(uuid) ? updatedSelectedLabels.delete(uuid) : updatedSelectedLabels.add(uuid);
    }

    return {
      ...state,
      selectedLabels: updatedSelectedLabels,
    };
  });

  public readonly selectMultipleLabels = this.updater((state, uuids: string[]) => {
    const updatedSelectedLabels = new Set(state.selectedLabels);

    uuids.forEach((id) => {
      updatedSelectedLabels.has(id) ? updatedSelectedLabels.delete(id) : updatedSelectedLabels.add(id);
    });

    return {
      ...state,
      selectedLabels: updatedSelectedLabels,
      multiSelection: uuids.length > 1 ? true : state?.multiSelection,
    };
  });

  public readonly removeLabels = this.updater((state, uuids: string[]) => {
    const updatedSelectedLabels = new Set(state.selectedLabels);

    uuids.forEach((id) => {
      updatedSelectedLabels.delete(id);
    });

    return {
      ...state,
      selectedLabels: updatedSelectedLabels,
    };
  });

  public readonly toggleShowPinnedOnly = this.updater((state) => ({
    ...state,
    showPinnedOnly: !state.showPinnedOnly,
  }));

  // Category updaters
  public readonly toggleCollapsedCategory = this.updater((state, uuid: string) => {
    const updatedCollapsed = new Set(state.collapsedCategories);
    updatedCollapsed.has(uuid) ? updatedCollapsed.delete(uuid) : updatedCollapsed.add(uuid);
    return {
      ...state,
      collapsedCategories: updatedCollapsed,
    };
  });

  // Selection updaters
  public readonly toggleMultiSelection = this.updater((state) => ({
    ...state,
    multiSelection: !state.multiSelection,
    selectedLabels: state.multiSelection ? new Set() : state.selectedLabels,
  }));

  public readonly setMultiSelection = this.updater((state, enabled: boolean) => ({
    ...state,
    multiSelection: enabled,
    selectedLabels: !enabled ? new Set() : state.selectedLabels,
  }));

  public readonly setCounterMode = this.updater((state, mode: CounterMode) => ({
    ...state,
    counterMode: mode,
  }));

  // Tab context updaters
  public readonly setTabContext = this.updater((state, context: ETabContext | undefined) => ({
    ...state,
    tabContext: state.tabContext !== context ? context : undefined,
  }));

  public readonly setAcquisitionEdition = this.updater((state, value: boolean) => ({
    ...state,
    acquisitionEdition: value,
  }));

  public readonly toggleEditing = this.updater((state, value: boolean) => ({
    ...state,
    editingEnabled: value,
  }));

  public readonly addViewedAsset = this.updater((state, assetId: string) => {
    const updatedViewedAssets = new Set(state.viewedAssets);
    updatedViewedAssets.add(assetId);
    return {
      ...state,
      viewedAssets: updatedViewedAssets,
    };
  });

  public readonly updateAssetFilters = this.updater((state, filter: 'analyzed' | 'favAcquisition' | 'favAnalysis') => {
    const currentFilters = state.assetFilters;
    return {
      ...state,
      assetFilters: {
        ...currentFilters,
        [filter]: !currentFilters[filter],
      },
    };
  });

  public readonly setBookmarkedAsset = this.updater((state, assetId: string | null) => ({
    ...state,
    bookmarkedAsset: assetId,
  }));

  public readonly selectRois = this.updater((state, roiIds: string[]) => ({
    ...state,
    selectedRois: new Set(roiIds),
  }));

  public readonly deselectRois = this.updater((state) => ({
    ...state,
    selectedRois: new Set<string>(),
  }));

  public readonly setAnalyzedAssets = this.updater((state, assetIds: string[]) => ({
    ...state,
    analyzedAssets: new Set(assetIds),
  }));

  public readonly setReviewedAssets = this.updater((state, assetIds: string[]) => ({
    ...state,
    reviewedAssets: new Set(assetIds),
  }));

  public readonly resetAnalyzedAssets = this.updater((state) => ({
    ...state,
    analyzedAssets: new Set<string>(),
  }));

  public readonly resetReviewedAssets = this.updater((state) => ({
    ...state,
    reviewedAssets: new Set<string>(),
  }));

  public readonly markAssetsAsAnalyzed = this.updater((state, assetIds: string[]) => {
    if (assetIds.length === 0) return state;
    const next = new Set(state.analyzedAssets);
    for (const id of assetIds) if (id) next.add(id);
    return { ...state, analyzedAssets: next };
  });

  public readonly markAssetsAsReviewed = this.updater((state, assetIds: string[]) => {
    if (assetIds.length === 0) return state;
    const next = new Set(state.reviewedAssets);
    for (const id of assetIds) if (id) next.add(id);
    return { ...state, reviewedAssets: next };
  });

  public readonly setViewedAssets = this.updater((state, assetIds: string[]) => ({
    ...state,
    viewedAssets: new Set(assetIds),
  }));

  public readonly updateBoxDimensionsForLabel = this.updater(
    (state, { labelId, dimensions }: { labelId: string; dimensions: BoxDimension }) => ({
      ...state,
      boxDimensions: new Map(state.boxDimensions).set(labelId, dimensions),
    })
  );

  public readonly setMultiSelectionBoxDimensionsForCombo = this.updater(
    (state, { comboKey, dimensions }: { comboKey: string; dimensions: BoxDimension }) => ({
      ...state,
      multiSelectionBoxDimensions: new Map(state.multiSelectionBoxDimensions).set(comboKey, dimensions),
    })
  );

  public readonly initializeBoxDimensions = this.updater((state, labels: ILabel[]) => {
    const newBoxDimensions = new Map(state.boxDimensions);

    labels.forEach((label) => {
      if (!newBoxDimensions.has(label.id)) {
        newBoxDimensions.set(label.id, {
          defaultWidth: DEFAULT_BOX_WIDTH,
          defaultHeight: DEFAULT_BOX_HEIGHT,
        });
      }
    });

    return {
      ...state,
      boxDimensions: newBoxDimensions,
    };
  });

  // #endregion

  public readonly updateImageFilters = this.effect<PresetFilters>((presetFilters$) =>
    presetFilters$.pipe(
      tap(({ preset, filters }) => {
        this.setSelectedPreset(preset);
        this.updateImageFiltersBulk(filters);
      })
    )
  );

  public readonly toggleBookmark = this.effect((assetId$: Observable<string>) =>
    assetId$.pipe(
      concatLatestFrom(() => this.bookmarkedAsset$),
      tap(([assetId, currentBookmarkedId]) => {
        const newBookmarkedId = currentBookmarkedId === assetId ? null : assetId;
        this.setBookmarkedAsset(newBookmarkedId);
      })
    )
  );

  public readonly fetchReferenceStripInfo = this.effect(
    (
      params$: Observable<{
        sampleId: string;
        analyst: Analyst;
        assetIds: string[];
        localFindings: FindingParameters[];
      }>
    ) =>
      params$.pipe(
        switchMap(({ sampleId, analyst, assetIds, localFindings }) => {
          if (!assetIds.length) return EMPTY;

          return from(
            this.getAssetStatusUseCase.execute({
              sampleId,
              assetIds,
              analystId: analyst.id,
              analystEntity: analyst.type,
              localFindings,
            })
          ).pipe(
            tap({
              next: ({ analyzedAssetIds, reviewedAssetIds }) => {
                this.setAnalyzedAssets(analyzedAssetIds);
                this.setReviewedAssets(reviewedAssetIds);
              },
              error: (error) => console.error(`Failed to fetch reference strip data: ${error.message}`),
            }),
            catchError((error) => {
              console.error(`Failed to fetch reference strip data: ${error.message}`);
              return EMPTY;
            })
          );
        })
      )
  );

  public createCurrentBoxDimensionsSelector(selectedLabels$: Observable<any[]>) {
    const defaultDimensions = {
      defaultWidth: DEFAULT_BOX_WIDTH,
      defaultHeight: DEFAULT_BOX_HEIGHT,
    };
    return this.select(
      selectedLabels$,
      this.boxDimensions$,
      this.multiSelectionBoxDimensions$,
      (selectedLabels, boxDimensions, multiSelectionBoxDimensions) => {
        if (selectedLabels.length === 0) {
          return defaultDimensions;
        }
        if (selectedLabels.length === 1) {
          return boxDimensions.get(selectedLabels[0].id) || defaultDimensions;
        }
        const comboKey = selectedLabels
          .map((l) => l.id)
          .slice()
          .sort()
          .join('|');
        return multiSelectionBoxDimensions.get(comboKey) || defaultDimensions;
      }
    );
  }
}
